import { useState, useEffect } from "react";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { toast } from "sonner";
import { Loader2, Plus, Send, Award, CheckCircle2, XCircle, FileText, Paperclip, Printer } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  useRfq, useRfqLookups, useUpdateRfq, useAssignVendors,
  usePublishRfq, useAwardQuotation, useApproveAward, useAwardForRfq, useVendorAwards,
  useTechnicalEvaluation, useNegotiation
} from "@/features/rfqs/api";
import { RfqStatusBadge } from "@/features/rfqs/RfqStatusBadge";
import { QuotationEntryDialog } from "@/features/rfqs/QuotationEntryDialog";
import { QuotationComparisonTable } from "@/features/rfqs/QuotationComparisonTable";
import { TechnicalEvaluationTab } from "@/features/rfqs/TechnicalEvaluationTab";
import { NegotiationTab } from "@/features/rfqs/NegotiationTab";
import { VendorAwardsPanel } from "@/features/rfqs/VendorAwardsPanel";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { fmtDateTime } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/rfqs/$rfqId/")({
  component: RfqDetail,
});

function RfqDetail() {
  const { rfqId } = Route.useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useRfq(rfqId);
  const { data: award } = useAwardForRfq(rfqId);
  const { data: lineAwards } = useVendorAwards(rfqId);
  const hasLineAwards = (lineAwards?.results.length ?? 0) > 0;
  const { data: lookups } = useRfqLookups();
  const { data: me } = useCurrentUser();
  const roles = me?.roles ?? [];
  const canApproveAward = roles.some((r) => r === "finance" || r === "management" || r === "super_admin");
  const canManage = roles.some((r) => r === "purchase_stores" || r === "super_admin");
  const canEvaluate = roles.some((r) => r === "biomed_head" || r === "super_admin");

  const { data: techEvals } = useTechnicalEvaluation(rfqId);
  const { data: negs } = useNegotiation(rfqId);

  const updateRfq = useUpdateRfq();
  const assignVendors = useAssignVendors();
  const publish = usePublishRfq();
  const awardQ = useAwardQuotation();
  const approve = useApproveAward();

  const [awardQuotationId, setAwardQuotationId] = useState<string | null>(null);
  const [selectedVendors, setSelectedVendors] = useState<string[]>([]);
  const [terms, setTerms] = useState("");
  const [pay, setPay] = useState("");
  const [del, setDel] = useState("");
  const [due, setDue] = useState("");
  const [approvalRemarks, setApprovalRemarks] = useState("");

  useEffect(() => {
    if (data?.rfq) {
      setTerms(data.rfq.terms_and_conditions ?? "");
      setPay(data.rfq.payment_terms ?? "");
      setDel(data.rfq.delivery_terms ?? "");
      setDue(data.rfq.due_date ?? "");
      setSelectedVendors((data.vendors ?? []).map((v) => v.vendor_id));
    }
  }, [data?.rfq?.id]); // eslint-disable-line

  if (isLoading || !data) return <div className="grid place-items-center p-10"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  const { rfq, items, vendors, quotations } = data;
  const isDraft = rfq.status === "Draft";
  const canPublish = isDraft && selectedVendors.length > 0;

  const saveHeader = async () => {
    try {
      await updateRfq.mutateAsync({
        id: rfq.id,
        patch: { terms_and_conditions: terms, payment_terms: pay, delivery_terms: del, due_date: due || null },
      });
      toast.success("Saved");
    } catch (e) { toast.error((e as Error).message); }
  };

  const saveVendors = async () => {
    try {
      await assignVendors.mutateAsync({ rfqId: rfq.id, vendorIds: selectedVendors });
      toast.success("Vendors updated");
    } catch (e) { toast.error((e as Error).message); }
  };

  const doPublish = async () => {
    try {
      if (selectedVendors.length !== vendors.length ||
          !selectedVendors.every((id) => vendors.find((v) => v.vendor_id === id))) {
        await assignVendors.mutateAsync({ rfqId: rfq.id, vendorIds: selectedVendors });
      }
      await publish.mutateAsync(rfq.id);
      toast.success("RFQ published");
    } catch (e) { toast.error((e as Error).message); }
  };

  const doAward = (quotationId: string) => {
    setAwardQuotationId(quotationId);
  };

  const confirmAward = async () => {
    if (!awardQuotationId) return;
    try {
      await awardQ.mutateAsync({ rfqId: rfq.id, quotationId: awardQuotationId });
      toast.success("Awarded — pending Finance approval");
      setAwardQuotationId(null);
    } catch (e) { toast.error((e as Error).message); }
  };

  const doApprove = async (decision: "Approved" | "Rejected") => {
    if (!award) return;
    try {
      await approve.mutateAsync({ awardId: award.id, decision, remarks: approvalRemarks || undefined });
      toast.success(`Award ${decision.toLowerCase()}`);
    } catch (e) { toast.error((e as Error).message); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight">{rfq.rfq_number}</h1>
            <RfqStatusBadge status={rfq.status} />
          </div>
          <p className="text-sm text-muted-foreground">
            {rfq.indent_details?.indent_no ? <>From indent <Link className="underline" to="/indents/$indentId" params={{ indentId: rfq.indent_details.id }}>{rfq.indent_details.indent_no}</Link> · </> : null}
            {rfq.branch_details?.name}
          </p>
        </div>
        <div className="flex gap-2">
          {canManage && isDraft && (
            <Button onClick={doPublish} disabled={!canPublish || publish.isPending}>
              <Send className="h-4 w-4 mr-1" /> Publish RFQ
            </Button>
          )}
          {award?.award_status === "Approved" && (
            award.po_id ? (
              <Button variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-50" onClick={() => navigate({ to: "/purchase-orders/$poId", params: { poId: award.po_id! } })}>
                <CheckCircle2 className="h-4 w-4 mr-1" /> PO Issued
              </Button>
            ) : (
              <Button onClick={() => navigate({ to: "/purchase-orders/new", search: { fromAward: award.id } })}>
                <FileText className="h-4 w-4 mr-1" /> Create PO
              </Button>
            )
          )}
        </div>
      </div>

      <Tabs defaultValue="items">
        <TabsList>
          <TabsTrigger value="items">Items &amp; Terms</TabsTrigger>
          <TabsTrigger value="vendors">Vendors</TabsTrigger>
          <TabsTrigger value="quotations">Quotations ({quotations.length})</TabsTrigger>
          <TabsTrigger value="compare">Comparison</TabsTrigger>
          <TabsTrigger value="tech-eval">Tech Eval</TabsTrigger>
          <TabsTrigger value="negotiation">Negotiation</TabsTrigger>
          <TabsTrigger value="award">Award</TabsTrigger>
        </TabsList>

        <TabsContent value="items" className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Line Items</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Item</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  {/* <TableHead>UoM</TableHead> */}
                </TableRow></TableHeader>
                <TableBody>
                  {items.map((it) => (
                    <TableRow key={it.id}>
                      <TableCell>{it.line_no}</TableCell>
                      <TableCell className="font-medium">{it.item_name}</TableCell>
                      <TableCell className="text-muted-foreground">{it.description ?? "—"}</TableCell>
                      <TableCell className="text-right">{Number(it.qty).toFixed(2)}</TableCell>
                      {/* <TableCell>{it.uom ?? "—"}</TableCell> */}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Terms &amp; Timeline</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label>Due Date</Label><Input type="date" value={due} onChange={(e) => setDue(e.target.value)} disabled={!canManage || !isDraft} /></div>
              <div className="md:col-span-2"><Label>Payment Terms</Label><Textarea rows={2} value={pay} onChange={(e) => setPay(e.target.value)} disabled={!canManage || !isDraft} /></div>
              <div className="md:col-span-2"><Label>Delivery Terms</Label><Textarea rows={2} value={del} onChange={(e) => setDel(e.target.value)} disabled={!canManage || !isDraft} /></div>
              <div className="md:col-span-2"><Label>Terms &amp; Conditions</Label><Textarea rows={5} value={terms} onChange={(e) => setTerms(e.target.value)} disabled={!canManage || !isDraft} /></div>
              <div className="md:col-span-2 flex justify-end gap-2 mt-2">
                <Button 
                  variant="secondary" 
                  onClick={() => navigate({ to: "/rfqs/$rfqId/print", params: { rfqId: rfq.id } })}
                  type="button"
                  disabled={items.length === 0 || !rfq.payment_terms || !rfq.delivery_terms}
                  title={items.length === 0 || !rfq.payment_terms || !rfq.delivery_terms ? "Save items, payment terms, and delivery terms before printing" : ""}
                >
                  <Printer className="mr-2 h-4 w-4" /> Print / PDF
                </Button>
                {canManage && isDraft && (
                  <Button onClick={saveHeader} disabled={updateRfq.isPending} variant="outline">Save Terms</Button>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="vendors">
          <Card>
            <CardHeader><CardTitle className="text-base">Assigned Vendors</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {canManage && isDraft && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-96 overflow-y-auto border rounded-md p-2">
                  {(lookups?.vendors ?? []).map((v) => {
                    const on = selectedVendors.includes(v.id);
                    return (
                      <label key={v.id} className="flex items-center gap-2 cursor-pointer p-1 hover:bg-muted rounded">
                        <input type="checkbox" checked={on} onChange={(e) => setSelectedVendors((s) => e.target.checked ? [...s, v.id] : s.filter((x) => x !== v.id))} />
                        <span className="text-sm">{v.name} <span className="text-muted-foreground">({v.code})</span></span>
                      </label>
                    );
                  })}
                </div>
              )}
              {vendors.length === 0 && !isDraft && <p className="text-sm text-muted-foreground">No vendors assigned.</p>}
              {vendors.length > 0 && (
                <Table>
                  <TableHeader><TableRow><TableHead>Vendor</TableHead><TableHead>Status</TableHead><TableHead>Sent At</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {vendors.map((v) => (
                      <TableRow key={v.id}>
                        <TableCell>{v.vendor?.name}</TableCell>
                        <TableCell><Badge variant="secondary">{v.status}</Badge></TableCell>
                        <TableCell>{v.sent_at ? fmtDateTime(v.sent_at) : "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {canManage && isDraft && (
                <div className="flex justify-end">
                  <Button onClick={saveVendors} disabled={assignVendors.isPending} variant="outline">Save Vendors</Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="quotations">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Vendor Quotations</CardTitle>
              {canManage && rfq.status !== "Draft" && rfq.status !== "Cancelled" && (
                <QuotationEntryDialog
                  key={`new-quotation-${quotations.length}`}
                  rfq={rfq} items={items} vendors={vendors} quotations={quotations} negs={negs ?? []}
                  trigger={<Button size="sm"><Plus className="h-4 w-4 mr-1" />Enter Quotation</Button>}
                />
              )}
            </CardHeader>
            <CardContent className="p-0">
              {quotations.length === 0 ? <div className="p-6 text-center text-sm text-muted-foreground">No quotations yet.</div> : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Quotation No</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Document</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {quotations.map((q) => (
                      <TableRow key={q.id}>
                        <TableCell className="font-medium">{q.vendor?.name}</TableCell>
                        <TableCell>{q.quotation_no ?? "—"}</TableCell>
                        <TableCell>{q.quotation_date ?? "—"}</TableCell>
                        <TableCell>
                          {q.document_url ? (
                            <a href={q.document_url} target="_blank" rel="noreferrer" className="text-primary hover:underline flex items-center gap-1">
                              <Paperclip className="h-4 w-4" /> View
                            </a>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell><Badge variant={q.status === "Selected" ? "default" : "secondary"}>{q.status}</Badge></TableCell>
                        <TableCell className="text-right tabular-nums">{Number(q.total_value).toFixed(2)}</TableCell>
                        <TableCell className="text-right space-x-1">
                          <QuotationEntryDialog rfq={rfq} items={items} vendors={vendors} existing={q} readOnly negs={negs ?? []}
                            trigger={<Button size="sm" variant="ghost">View</Button>} />
                          {canManage && (
                            <QuotationEntryDialog rfq={rfq} items={items} vendors={vendors} existing={q} negs={negs ?? []}
                              trigger={<Button size="sm" variant="ghost">Edit</Button>} />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="compare">
          <QuotationComparisonTable
            items={items}
            quotations={quotations}
            defaultTemplateId={(rfq as unknown as { comparison_template_id?: string | null }).comparison_template_id ?? null}
            rfqId={rfq.id}
            canManage={canManage}
          />
        </TabsContent>

        <TabsContent value="tech-eval">
          <TechnicalEvaluationTab 
            rfq={rfq} items={items} quotations={quotations} 
            techEvals={techEvals ?? []} canEvaluate={canEvaluate} 
          />
        </TabsContent>

        <TabsContent value="negotiation">
          <NegotiationTab 
            rfq={rfq} items={items} quotations={quotations} 
            negs={negs ?? []} canManage={canManage} 
          />
        </TabsContent>
        <TabsContent value="award" className="space-y-3">
          {(!award || award.award_status === "Rejected") && <VendorAwardsPanel rfqId={rfq.id} />}
          {!award && !hasLineAwards && canManage && quotations.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Single-vendor award (legacy) — select winning quotation</CardTitle></CardHeader>
              <CardContent>
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Vendor</TableHead>
                    <TableHead className="text-right">Quotation Total</TableHead>
                    <TableHead className="text-right">Negotiated Total</TableHead>
                    <TableHead className="text-center">Tech Eval</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {[...quotations].sort((a, b) => Number(a.total_value) - Number(b.total_value)).map((q, i) => {
                      const neg = (negs ?? []).find(n => n.vendor === q.vendor_id);
                      const hasNegotiation = neg && neg.negotiated_amount !== null && neg.negotiated_amount !== undefined;
                      const techEval = (techEvals ?? []).find(e => e.vendor === q.vendor_id);
                      const score = techEval?.items?.reduce((sum, item) => sum + (item.tech_score ?? 0), 0) ?? 0;
                      const hasEval = !!techEval;
                      const isRecommended = techEval?.recommended;
                      const isRejected = techEval?.status === "rejected";
                      return (
                      <TableRow key={q.id}>
                        <TableCell>{q.vendor?.name} {i === 0 && <Badge className="ml-2 bg-emerald-600">L1</Badge>}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          <span className={hasNegotiation ? "line-through text-muted-foreground" : ""}>
                            {Number(q.total_value).toFixed(2)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {hasNegotiation ? <span className="font-medium text-emerald-600">{Number(neg.negotiated_amount).toFixed(2)}</span> : "—"}
                        </TableCell>
                        <TableCell className="text-center">
                          {hasEval ? (
                            <div className="flex flex-col items-center gap-1">
                              <span className="text-sm font-medium">{score} pts</span>
                              {isRecommended && <Badge className="bg-emerald-600">Recommended</Badge>}
                              {isRejected && <Badge variant="destructive">Rejected</Badge>}
                              {!isRecommended && !isRejected && <Badge variant="secondary">{techEval.status}</Badge>}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" onClick={() => doAward(q.id)}><Award className="h-4 w-4 mr-1" />Award</Button>
                        </TableCell>
                      </TableRow>
                    )})}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
          {award && (
            <Card>
              <CardHeader><CardTitle className="text-base">Award — {award.awarded_vendor_name ?? quotations.find(q => q.id === award.quotation_id)?.vendor?.name ?? "Unknown Vendor"}</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-3 text-sm">
                  <Badge className={award.award_status === "Approved" ? "bg-emerald-600" : award.award_status === "Rejected" ? "bg-rose-600" : "bg-amber-500"}>
                    {award.award_status}
                  </Badge>
                  <span>Awarded on {award.awarded_at ? fmtDateTime(award.awarded_at) : "—"}</span>
                  {award.approved_at && <span>· {award.award_status} on {fmtDateTime(award.approved_at)}</span>}
                </div>
                <div className="text-sm">
                  <span className="text-muted-foreground">Awarded Price: </span>
                  <span className="font-semibold tracking-tight">{new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(award.awarded_amount ?? 0)}</span>
                </div>
                {award.approval_remarks && <p className="text-sm text-muted-foreground">Remarks: {award.approval_remarks}</p>}
                {award.award_status === "Pending" && canApproveAward && (
                  <div className="space-y-2 border-t pt-3">
                    <Label>Approval Remarks</Label>
                    <Textarea rows={2} value={approvalRemarks} onChange={(e) => setApprovalRemarks(e.target.value)} />
                    <div className="flex gap-2">
                      <Button onClick={() => doApprove("Approved")} disabled={approve.isPending}>
                        <CheckCircle2 className="h-4 w-4 mr-1" />Approve Award
                      </Button>
                      <Button variant="outline" onClick={() => doApprove("Rejected")} disabled={approve.isPending}>
                        <XCircle className="h-4 w-4 mr-1" />Reject
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      <AlertDialog open={!!awardQuotationId} onOpenChange={(open) => !open && setAwardQuotationId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Award this vendor?</AlertDialogTitle>
            <AlertDialogDescription>
              This will select this vendor's quotation and send the Award to Finance for final approval.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmAward}>Confirm Award</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
